import { useEffect } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Icon } from "@dtw/icons";
import { getKlantDossier } from "@/features/klanten/klanten.api";
import { Status } from "@/components/ui/Status";
import { useBreadcrumb } from "@/components/shell/BreadcrumbContext";
import { fmtMoney, fmtDate, fmtDayTime } from "@/lib/format";
import type { Contactpersoon, KlantDossier, Onderdeel } from "@/lib/types";

// Het klantdossier. De klant staat centraal: dit is het startpunt, de
// abonnementenlijst blijft de werklijst.
export const Route = createFileRoute("/_shell/klanten/$id")({
  component: KlantDossierPage,
});

function KlantDossierPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const { setDetail } = useBreadcrumb();

  const q = useQuery({ queryKey: ["klant", id, "dossier"], queryFn: () => getKlantDossier(id) });
  const d = q.data;

  useEffect(() => {
    setDetail(d?.klant.naam ?? null);
    return () => setDetail(null);
  }, [d?.klant.naam, setDetail]);

  if (q.isLoading) return <div className="app-page">{t("common.loading")}</div>;
  if (!d) {
    return (
      <div className="app-page">
        <div className="panel">
          <div className="empty">
            <div className="empty__icon">
              <Icon name="alert-circle" size={22} />
            </div>
            <h3>{t("common.error")}</h3>
          </div>
        </div>
      </div>
    );
  }

  const { klant } = d;
  const maandTotaal = d.abonnementen.reduce((som, a) => som + a.maandbedragCents, 0);

  return (
    <>
      <div className="dossier-head">
        <div className="dossier-head__main">
          <div className="idrow">
            <span className="did">{klant.naam}</span>
            <span className={`dtw-chip${klant.type === "klant" ? " dtw-chip--indigo" : ""}`}>
              {klant.type === "klant" ? t("klanten.type.klant") : t("klanten.type.prospect")}
            </span>
          </div>
          <div className="metarow">
            {klant.plaats && <span>{klant.plaats}</span>}
            {klant.kvk && (
              <>
                <span className="sep">·</span> {t("klant.field.kvk")}{" "}
                <span className="mono">{klant.kvk}</span>
              </>
            )}
            {klant.klantSinds && (
              <>
                <span className="sep">·</span> {t("klant.field.sinds")} {klant.klantSinds}
              </>
            )}
          </div>
        </div>
        <div className="dossier-head__aside">
          <span className="dtw-chip">
            {fmtMoney(maandTotaal)} {t("pakketten.perMaand")}
          </span>
        </div>
      </div>

      <div className="app-page" style={{ paddingTop: "var(--space-5)" }}>
        <div className="grid">
          <div className="dtw-vstack" style={{ gap: "var(--space-4)" }}>
            {/* Abonnementen met hun onderdelen — het hele beeld in één blik. */}
            {d.abonnementen.length === 0 ? (
              <div className="dtw-card">
                <div className="dtw-card__body">
                  <p className="t-tertiary">{t("klant.geenAbonnementen")}</p>
                </div>
              </div>
            ) : (
              d.abonnementen.map((a) => (
                <div className="dtw-card" key={a.id}>
                  <div className="dtw-card__header">
                    <span className="dtw-card__title">
                      <Link to="/abonnementen/$id" params={{ id: a.nummer }} className="mono">
                        {a.nummer}
                      </Link>
                    </span>
                    <Status status={a.status} />
                  </div>
                  <div className="dtw-card__body">
                    <dl className="kv">
                      <dt>{t("abonnement.field.maandbedrag")}</dt>
                      <dd className="mono">{fmtMoney(a.maandbedragCents)}</dd>
                      <dt>{t("abonnement.field.jaarbedrag")}</dt>
                      <dd className="mono">{fmtMoney(a.jaarbedragCents)}</dd>
                      <dt>{t("abonnement.field.verlengtOp")}</dt>
                      <dd className="mono">{fmtDate(a.verlengtOp)}</dd>
                      <dt>{t("abonnement.field.opzegbaarTot")}</dt>
                      <dd className="mono">{fmtDate(a.opzegbaarTot)}</dd>
                    </dl>
                  </div>
                  <div className="dtw-card__body flush">
                    <OnderdelenTabel onderdelen={a.onderdelen} />
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="dtw-vstack" style={{ gap: "var(--space-4)" }}>
            <ContactpersonenCard contactpersonen={klant.contactpersonen} />
            <LeadsCard leads={d.leads} />
            <MomentenCard momenten={d.contactmomenten} />
            <TijdlijnCard events={d.tijdlijn} />
          </div>
        </div>
      </div>
    </>
  );
}

function OnderdelenTabel({ onderdelen }: { onderdelen: Onderdeel[] }) {
  const { t } = useTranslation();
  if (onderdelen.length === 0) {
    return (
      <div className="empty">
        <h3>{t("onderdelen.empty.title")}</h3>
      </div>
    );
  }
  return (
    <div className="dtw-table-wrap">
      <table className="dtw-table">
        <thead>
          <tr>
            <th>{t("onderdelen.col.productcode")}</th>
            <th>{t("onderdelen.col.onderdeel")}</th>
            <th>{t("onderdelen.col.status")}</th>
            <th className="num">{t("onderdelen.col.bedrag")}</th>
            <th>{t("onderdelen.col.ingangsdatum")}</th>
          </tr>
        </thead>
        <tbody>
          {onderdelen.map((o) => (
            <tr key={o.id} className={o.status === "vervallen" ? "is-muted" : ""}>
              <td className="mono">{o.pakket.productcode}</td>
              <td>
                {o.pakket.productgroepLabel} {o.pakket.model}
                <span className="t-tertiary"> · {o.pakket.niveauLabel}</span>
              </td>
              <td>
                <Status status={o.status} />
              </td>
              <td className="num mono">{fmtMoney(o.overeengekomenMaandbedragCents)}</td>
              <td className="mono">
                {o.ingangsdatum ? (
                  fmtDate(o.ingangsdatum)
                ) : (
                  <span className="t-tertiary">{t("onderdelen.geenDatum")}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Geen rollentaxonomie: een vrije functie plus de twee markeringen die het systeem
// moet kunnen opzoeken — naar wie de factuur gaat, en wie mag ondertekenen.
function ContactpersonenCard({ contactpersonen }: { contactpersonen: Contactpersoon[] }) {
  const { t } = useTranslation();
  return (
    <div className="dtw-card">
      <div className="dtw-card__header">
        <span className="dtw-card__title">{t("klant.section.contactpersonen")}</span>
        <span className="dtw-chip">{contactpersonen.length}</span>
      </div>
      <div className="dtw-card__body">
        {contactpersonen.length === 0 ? (
          <p className="t-tertiary">{t("klant.contact.geen")}</p>
        ) : (
          <div className="contact-list">
            {contactpersonen.map((c) => (
              <div className="contact-row" key={c.id}>
                <span className="contact-row__naam">{c.naam}</span>
                {c.functie && <span className="contact-row__functie">{c.functie}</span>}
                <span className="dtw-spacer" />
                <span className="contact-row__marks">
                  {c.isPrimair && <span className="dtw-chip dtw-chip--indigo">{t("klant.contact.primair")}</span>}
                  {c.isFacturatiecontact && <span className="dtw-chip">{t("klant.contact.facturatie")}</span>}
                  {c.isOndertekenaar && <span className="dtw-chip">{t("klant.contact.ondertekenaar")}</span>}
                </span>
                {c.email && (
                  <a href={`mailto:${c.email}`} className="contact-row__functie">
                    {c.email}
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Openstaande verkoopkansen bij deze klant — het voorstadium van een proef of
// bestelling, in hetzelfde dossier als de contracten.
function LeadsCard({ leads }: { leads: KlantDossier["leads"] }) {
  const { t } = useTranslation();
  if (leads.length === 0) return null;
  return (
    <div className="dtw-card">
      <div className="dtw-card__header">
        <span className="dtw-card__title">{t("leads.title")}</span>
        <span className="dtw-chip">{leads.length}</span>
      </div>
      <div className="dtw-card__body">
        <div className="contact-list">
          {leads.map((l) => (
            <div className="contact-row" key={l.id}>
              <span className={`dtw-chip lead-kwal lead-kwal--${l.kwalificatie}`}>
                {l.kwalificatie.toUpperCase()}
              </span>
              <span className="contact-row__naam">{l.omschrijving}</span>
              <span className="dtw-spacer" />
              <span className="contact-row__functie mono">
                {fmtMoney(l.verwachtMaandbedragCents)} · {l.verwachtJaar}-
                {String(l.verwachtMaand).padStart(2, "0")}
              </span>
              {l.status !== "open" && (
                <span className="dtw-chip">{t(`leads.status.${l.status}` as never)}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Gespreksnotities en vervolgacties. Openstaande acties eerst: dat is de lijst
// waarop iemand actie moet ondernemen.
function MomentenCard({ momenten }: { momenten: KlantDossier["contactmomenten"] }) {
  const { t } = useTranslation();
  const gesorteerd = [...momenten].sort((a, b) => Number(b.open) - Number(a.open));
  return (
    <div className="dtw-card">
      <div className="dtw-card__header">
        <span className="dtw-card__title">{t("momenten.title")}</span>
        <span className="dtw-chip">{momenten.length}</span>
      </div>
      <div className="dtw-card__body">
        {momenten.length === 0 ? (
          <p className="t-tertiary">{t("momenten.geen")}</p>
        ) : (
          <div className="moment-list">
            {gesorteerd.map((m) => (
              <div
                className={`moment${m.open ? " is-open" : m.afgerondOp ? " is-afgerond" : ""}`}
                key={m.id}
              >
                <div className="moment__kop">
                  <span className="mono">{fmtDate(m.datum)}</span>
                  <span>{m.auteurNaam}</span>
                  {m.type === "vervolgactie" && m.vervolgOp && (
                    <span className={m.open ? "t-warning" : "t-tertiary"}>
                      {t("momenten.terugbellenOp", { datum: fmtDate(m.vervolgOp) })}
                      {m.afgerondOp ? ` · ${t("momenten.afgerond")}` : ""}
                    </span>
                  )}
                </div>
                <div className="moment__tekst">{m.tekst}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TijdlijnCard({ events }: { events: KlantDossier["tijdlijn"] }) {
  const { t } = useTranslation();
  return (
    <div className="dtw-card">
      <div className="dtw-card__header">
        <span className="dtw-card__title">{t("klant.section.tijdlijn")}</span>
      </div>
      <div className="dtw-card__body">
        {events.length === 0 ? (
          <p className="t-tertiary">{t("common.none")}</p>
        ) : (
          <div className="timeline">
            {events.slice(0, 12).map((e) => (
              <div className="tl-step" key={e.id}>
                <div className="tl-dot">
                  <Icon name="check" size={11} />
                </div>
                <div className="tl-body">
                  <div className="tl-time">{fmtDayTime(e.at)}</div>
                  <div className="tl-title">{e.eventType}</div>
                  {typeof (e.payload as { actorName?: string }).actorName === "string" && (
                    <div className="tl-meta">Door {(e.payload as { actorName?: string }).actorName}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
